import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Tab,
  Tabs,
  Stack,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  Breadcrumbs,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Pagination,
  FormControl,
  Select,
  InputLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';

// Icons
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import PreviewIcon from '@mui/icons-material/Preview';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import HomeIcon from '@mui/icons-material/Home';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';

// Legal Icons
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BalanceIcon from '@mui/icons-material/Balance';

import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { moveDocument, moveFolder, LegalDocument } from '../../services/documentService';
import { useDocuments, useFolders, useDeleteDocument } from '../../hooks/useDocuments';
import DocumentListSkeleton from '../../components/skeletons/DocumentListSkeleton';
import { useQueryClient } from '@tanstack/react-query';

// Interfaces
interface LocalDocument {
  id: string;
  title: string;
  document_type: string;
  folder_id?: string;
  created_at: string | Date;
  updated_at: string | Date;
  source?: 'traditional' | 'ai_generated' | string;
}

interface DocumentFolder {
  id: string;
  name: string;
  parent_id?: string | null;
  created_at: string | Date;
  updated_at: string | Date;
  document_count?: number;
  subfolder_count?: number;
}

interface DocumentCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  count: number;
}

const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ⚡ React Query hooks - cache automático de 5 minutos
  const { data: documentsData, isLoading: documentsLoading, error: documentsError } = useDocuments();
  const { data: foldersData, isLoading: foldersLoading } = useFolders();
  const deleteDocumentMutation = useDeleteDocument();

  // Dados reais (com fallback para array vazio) - memoizados para evitar re-renders
  const documents = useMemo(() => documentsData || [], [documentsData]);
  const folders = useMemo(() => foldersData || [], [foldersData]);
  const loading = documentsLoading || foldersLoading;
  const error = documentsError ? (documentsError as any).message : null;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<LegalDocument | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDocumentDialog, setCreateDocumentDialog] = useState(false);
  
  // Estados para gerenciamento de pastas
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<DocumentFolder | null>(null);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Estados para paginação e busca
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30); // 5 linhas x 6 itens = 30
  const [searchResults, setSearchResults] = useState<{
    documents: LegalDocument[];
    folders: DocumentFolder[];
    total: number;
  }>({ documents: [], folders: [], total: 0 });

  // Estados para filtros e ordenação
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterType, setFilterType] = useState('all');
  
  // Estados para drag and drop
  const [draggedItem, setDraggedItem] = useState<{ type: 'document' | 'folder'; id: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  
  // Estados para confirmação de delete de pasta
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<DocumentFolder | null>(null);
  const [folderDeleteInfo, setFolderDeleteInfo] = useState<{ document_count: number; subfolder_count: number } | null>(null);

  // Categorias de documentos
  const documentCategories: DocumentCategory[] = [
    {
      id: 'all',
      name: 'Todos',
      icon: <DescriptionIcon />,
      color: '#1976d2',
      description: 'Todos os documentos',
      count: documents.length
    },
    {
      id: 'ai_generated',
      name: 'Gerados por IA',
      icon: <AutoAwesomeIcon />,
      color: '#9c27b0',
      description: 'Documentos criados com inteligência artificial',
      count: documents.filter(d => d.source === 'ai_generated' || d.document_type?.includes('IA')).length
    },
    {
      id: 'civil',
      name: 'Cível',
      icon: <BalanceIcon />,
      color: '#2196f3',
      description: 'Documentos de direito civil',
      count: documents.filter(d => d.document_type?.toLowerCase().includes('civ')).length
    },
    {
      id: 'penal',
      name: 'Penal',
      icon: <GavelIcon />,
      color: '#f44336',
      description: 'Documentos de direito penal',
      count: documents.filter(d => d.document_type?.toLowerCase().includes('penal')).length
    },
    {
      id: 'trabalhista',
      name: 'Trabalhista',
      icon: <WorkIcon />,
      color: '#ff9800',
      description: 'Documentos de direito trabalhista',
      count: documents.filter(d => d.document_type?.toLowerCase().includes('trabalhista')).length
    },
    {
      id: 'tributario',
      name: 'Tributário',
      icon: <AttachMoneyIcon />,
      color: '#4caf50',
      description: 'Documentos de direito tributário',
      count: documents.filter(d => d.document_type?.toLowerCase().includes('tributario')).length
    }
  ];

  // Opções de ordenação
  const sortOptions = [
    { value: 'date_desc', label: 'Mais recentes' },
    { value: 'date_asc', label: 'Mais antigos' },
    { value: 'name_asc', label: 'Nome (A-Z)' },
    { value: 'name_desc', label: 'Nome (Z-A)' },
    { value: 'type_asc', label: 'Tipo (A-Z)' },
    { value: 'updated_desc', label: 'Última modificação' },
    { value: 'created_desc', label: 'Recém criados' }
  ];

  // Opções de filtro por tipo de origem
  const typeFilterOptions = [
    { value: 'all', label: 'Todos os tipos' },
    { value: 'ai_generated', label: 'Gerados por IA' },
    { value: 'traditional', label: 'Tradicionais' },
    { value: 'petition', label: 'Petições' },
    { value: 'contract', label: 'Contratos' },
    { value: 'defense', label: 'Defesas' },
    { value: 'appeal', label: 'Recursos' }
  ];

  // Carregar documentos
  // ⚡ Não precisamos mais de useEffect para carregar dados!
  // React Query faz isso automaticamente com cache inteligente

  // Escutar atualizações de documentos (quando título é editado)
  useEffect(() => {
    const handleDocumentUpdate = (event: any) => {
      const { id, title } = event.detail;
      
      // ⚡ Atualizar documento localmente no cache React Query (optimistic update)
      queryClient.setQueryData(['documents'], (oldDocs: any) => {
        if (!oldDocs) return oldDocs;
        return oldDocs.map((doc: any) => 
          doc.id === id ? { ...doc, title, updated_at: new Date().toISOString() } : doc
        );
      });
    };

    window.addEventListener('document-updated', handleDocumentUpdate);
    return () => {
      window.removeEventListener('document-updated', handleDocumentUpdate);
    };
  }, [queryClient]);

  // ⚡ REMOVIDO: loadDocuments e loadFolders
  // Agora usamos React Query que gerencia cache automaticamente
  // Para recarregar: queryClient.invalidateQueries(['documents'])
  
  const refreshDocuments = () => {
    queryClient.invalidateQueries({ queryKey: ['documents'] });
  };
  
  const refreshFolders = () => {
    queryClient.invalidateQueries({ queryKey: ['folders'] });
  };

  // Funções para gerenciamento de pastas
  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const response = await api.post('/documents/folders', {
        name: newFolderName,
        parent_id: currentFolderId
      });
      
      if (response.data.status === 'success') {
        refreshFolders(); // ⚡ React Query refetch
        setCreateFolderDialogOpen(false);
        setNewFolderName('');
      }
    } catch (err: any) {
      console.error('Erro ao criar pasta:', err);
      alert(err.response?.data?.detail || 'Erro ao criar pasta');
    }
  };

  const handleDeleteFolderClick = async (folder: DocumentFolder) => {
    try {
      // Buscar informações sobre a pasta
      const response = await api.get(`/documents/folders/${folder.id}/info`);
      const info = response.data.data;
      
      setFolderToDelete(folder);
      setFolderDeleteInfo(info);
      setDeleteFolderDialogOpen(true);
      setFolderMenuAnchor(null);
    } catch (err: any) {
      console.error('Erro ao obter informações da pasta:', err);
      alert(err.response?.data?.detail || 'Erro ao obter informações da pasta');
    }
  };

  const confirmDeleteFolder = async (deleteContents: boolean) => {
    if (!folderToDelete) return;
    
    try {
      await api.delete(`/documents/folders/${folderToDelete.id}`, {
        params: { delete_contents: deleteContents }
      });
      // ⚡ React Query refetch
      refreshFolders();
      refreshDocuments();
      setDeleteFolderDialogOpen(false);
      setFolderToDelete(null);
      setFolderDeleteInfo(null);
    } catch (err: any) {
      console.error('Erro ao excluir pasta:', err);
      alert(err.response?.data?.detail || 'Erro ao excluir pasta');
    }
  };

  const moveDocumentToFolder = async (documentId: string, folderId: string | null) => {
    try {
      await api.put(`/documents/${documentId}`, {
        folder_id: folderId
      });
      refreshDocuments(); // ⚡ React Query refetch
    } catch (err: any) {
      console.error('Erro ao mover documento:', err);
      alert(err.response?.data?.detail || 'Erro ao mover documento');
    }
  };

  const toggleFolderExpansion = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleFolderMenuOpen = (event: React.MouseEvent<HTMLElement>, folder: DocumentFolder) => {
    setFolderMenuAnchor(event.currentTarget);
    setSelectedFolder(folder);
  };

  const handleFolderMenuClose = () => {
    setFolderMenuAnchor(null);
    setSelectedFolder(null);
  };

  // Handlers para Drag and Drop
  const handleDragStart = (e: React.DragEvent, itemType: 'document' | 'folder', itemId: string) => {
    setDraggedItem({ type: itemType, id: itemId });
    e.dataTransfer.effectAllowed = 'move';
    // Adiciona uma visualização simplificada durante o arraste
    if (e.dataTransfer.setDragImage) {
      const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
      dragImage.style.opacity = '0.5';
      e.dataTransfer.setDragImage(dragImage, 0, 0);
    }
  };

  const handleDragOver = (e: React.DragEvent, targetFolderId: string | null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(targetFolderId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Verifica se realmente saiu do elemento (e não apenas de um filho)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDropTarget(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetFolderId: string | null) => {
    e.preventDefault();
    
    if (!draggedItem) return;

    try {
      if (draggedItem.type === 'document') {
        // Mover documento para a pasta
        await moveDocument(draggedItem.id, targetFolderId);
        // ⚡ React Query refetch automático
        refreshDocuments();
        refreshFolders();
      } else if (draggedItem.type === 'folder') {
        // Impedir mover pasta para dentro dela mesma
        if (draggedItem.id === targetFolderId) {
          alert('Não é possível mover uma pasta para dentro dela mesma');
          return;
        }
        
        // Mover pasta para dentro de outra pasta
        await moveFolder(draggedItem.id, targetFolderId);
        // ⚡ React Query refetch automático
        refreshFolders();
        refreshDocuments();
      }
    } catch (err: any) {
      console.error('Erro ao mover item:', err);
      alert(err.response?.data?.detail || 'Erro ao mover item');
    } finally {
      setDraggedItem(null);
      setDropTarget(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTarget(null);
  };

  // Função de ordenação
  const sortItems = (docs: LegalDocument[], folders: DocumentFolder[], sortBy: string) => {
    let sortedDocs = [...docs];
    let sortedFolders = [...folders];

    switch (sortBy) {
      case 'date_desc':
        sortedDocs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        sortedFolders.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'date_asc':
        sortedDocs.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        sortedFolders.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'name_asc':
        sortedDocs.sort((a, b) => a.title.localeCompare(b.title));
        sortedFolders.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name_desc':
        sortedDocs.sort((a, b) => b.title.localeCompare(a.title));
        sortedFolders.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'type_asc':
        sortedDocs.sort((a, b) => a.document_type.localeCompare(b.document_type));
        break;
      case 'updated_desc':
        sortedDocs.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        sortedFolders.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        break;
      case 'created_desc':
        sortedDocs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        sortedFolders.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
    }

    return { documents: sortedDocs, folders: sortedFolders };
  };

  // Função de busca melhorada
  const performSearch = useCallback((term: string, category: string, folderId: string | null) => {
    let allDocuments = documents;
    let allFolders = folders;

    // Se há termo de busca, busca em todos os itens
    if (term.trim()) {
      allDocuments = documents.filter(doc => 
        doc.title.toLowerCase().includes(term.toLowerCase()) ||
        doc.document_type.toLowerCase().includes(term.toLowerCase())
      );
      
      allFolders = folders.filter(folder =>
        folder.name.toLowerCase().includes(term.toLowerCase())
      );
    } else {
      // Sem busca, filtra apenas pela pasta atual
      allDocuments = documents.filter(doc => {
        // Normalizar valores para comparação (null, undefined, "" são todos tratados como "sem pasta")
        const docFolderId = doc.folder_id || null;
        const targetFolderId = folderId || null;
        
        if (targetFolderId === null) {
          // Mostrando raiz: documentos sem pasta
          return docFolderId === null;
        } else {
          // Mostrando pasta específica: documentos com esse folder_id
          return docFolderId === targetFolderId;
        }
      });
      
      allFolders = folders.filter(folder => {
        const folderParentId = folder.parent_id || null;
        const targetFolderId = folderId || null;
        return folderParentId === targetFolderId;
      });
    }

    // Aplicar filtro de categoria
    if (category !== 'all') {
      allDocuments = allDocuments.filter(doc => {
        switch (category) {
          case 'ai_generated':
            return doc.source === 'ai_generated';
          case 'civil':
            return doc.document_type?.toLowerCase().includes('civ');
          case 'penal':
            return doc.document_type?.toLowerCase().includes('penal');
          case 'trabalhista':
            return doc.document_type?.toLowerCase().includes('trabalhista');
          case 'tributario':
            return doc.document_type?.toLowerCase().includes('tributario');
          default:
            return true;
        }
      });
    }

    // Aplicar filtro por tipo de origem
    if (filterType !== 'all') {
      allDocuments = allDocuments.filter(doc => {
        switch (filterType) {
          case 'ai_generated':
            return doc.source === 'ai_generated';
          case 'traditional':
            return doc.source === 'traditional';
          case 'petition':
            return doc.document_type?.toLowerCase().includes('petição') || 
                   doc.document_type?.toLowerCase().includes('petition');
          case 'contract':
            return doc.document_type?.toLowerCase().includes('contrato') || 
                   doc.document_type?.toLowerCase().includes('contract');
          case 'defense':
            return doc.document_type?.toLowerCase().includes('defesa') || 
                   doc.document_type?.toLowerCase().includes('defense');
          case 'appeal':
            return doc.document_type?.toLowerCase().includes('recurso') || 
                   doc.document_type?.toLowerCase().includes('appeal');
          default:
            return true;
        }
      });
    }

    // Aplicar ordenação
    const sorted = sortItems(allDocuments, allFolders, sortBy);

    return {
      documents: sorted.documents,
      folders: sorted.folders,
      total: sorted.documents.length + sorted.folders.length
    };
  }, [documents, folders, sortBy, filterType]);

  // Atualizar resultados quando houver mudanças
  useEffect(() => {
    const results = performSearch(searchTerm, selectedCategory, currentFolderId);
    setSearchResults(results);
    setCurrentPage(1); // Reset para primeira página
  }, [searchTerm, selectedCategory, currentFolderId, performSearch]);

  // Obter itens paginados
  const getPaginatedItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    const allItems = [
      ...searchResults.folders.map(folder => ({ ...folder, type: 'folder' as const })),
      ...searchResults.documents.map(document => ({ ...document, type: 'document' as const }))
    ];
    
    return {
      items: allItems.slice(startIndex, endIndex),
      totalPages: Math.ceil(allItems.length / itemsPerPage),
      totalItems: allItems.length
    };
  };

  const paginatedData = getPaginatedItems();
  const currentFolders = paginatedData.items.filter(item => item.type === 'folder') as (DocumentFolder & { type: 'folder' })[];
  const filteredDocuments = paginatedData.items.filter(item => item.type === 'document') as (LegalDocument & { type: 'document' })[];
  
  // Criar breadcrumb path
  const getBreadcrumbPath = () => {
    const path = [];
    let currentFolder = folders.find(f => f.id === currentFolderId);
    
    while (currentFolder) {
      path.unshift(currentFolder);
      currentFolder = folders.find(f => f.id === currentFolder?.parent_id);
    }
    
    return path;
  };

  // Handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, document: LegalDocument) => {
    setMenuAnchor(event.currentTarget);
    setSelectedDocument(document);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedDocument(null);
  };

  const handleDownload = async (document: LegalDocument) => {
    try {
      if (document.source === 'ai_generated') {
        const response = await api.get(`/ai-documents/document/${document.id}/download-docx`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = window.document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${document.title}.docx`);
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Erro ao baixar documento:', err);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;
    
    try {
      await api.delete(`/documents/${selectedDocument.id}`);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setDeleteDialogOpen(false);
      handleMenuClose();
    } catch (err) {
      console.error('Erro ao excluir documento:', err);
    }
  };

  const handlePreview = async (document: LegalDocument) => {
    // Redireciona diretamente para o editor avançado
    navigate(`/documents/editor-advanced/${document.id}`);
    handleMenuClose();
  };

  const handleFillFields = (document: LegalDocument) => {
    navigate(`/documents/fields/${document.id}`);
    handleMenuClose();
  };

  const handleEditDocument = (document: LegalDocument) => {
    navigate(`/documents/editor-advanced/${document.id}`);
    handleMenuClose();
  };

  const handleDuplicateDocument = async (document: LegalDocument) => {
    try {
      // Buscar conteúdo do documento original
      const response = await api.get(`/ai-documents/document/${document.id}/preview`);
      
      if (response.data.status === 'success') {
        const originalDoc = response.data.data;
        
        // Criar cópia do documento
        const duplicateResponse = await api.post('/ai-documents/generate-from-wizard', {
          nome_acao: `${originalDoc.title} (Cópia)`,
          descricao_fatos: 'Documento duplicado',
          area_direito: originalDoc.document_type || 'civil',
          is_duplicate: true,
          original_content: originalDoc.content,
          original_title: originalDoc.title
        });
        
        if (duplicateResponse.data.status === 'success') {
          queryClient.invalidateQueries({ queryKey: ['documents'] });
          // Navegar para o novo documento
          navigate(`/documents/editor-advanced/${duplicateResponse.data.data.document_id}`);
        }
      }
    } catch (err: any) {
      console.error('Erro ao duplicar documento:', err);
      // Fallback: criar via endpoint simples
      try {
        const response = await api.get(`/ai-documents/document/${document.id}/preview`);
        if (response.data.status === 'success') {
          const originalDoc = response.data.data;
          
          // Criar documento duplicado diretamente
          const createResponse = await api.post('/ai-documents/create-simple', {
            title: `${originalDoc.title} (Cópia)`,
            content: originalDoc.content,
            document_type: originalDoc.document_type || 'civil'
          });
          
          if (createResponse.data.status === 'success') {
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            navigate(`/documents/editor-advanced/${createResponse.data.data.id}`);
          }
        }
      } catch (fallbackErr) {
        console.error('Erro no fallback ao duplicar:', fallbackErr);
        alert('Erro ao duplicar documento');
      }
    }
    handleMenuClose();
  };

  const formatDate = (dateValue: string | Date) => {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceBadge = (source?: string) => {
    if (source === 'ai_generated') {
      return (
        <Chip 
          icon={<AutoAwesomeIcon />}
          label="IA"
          size="small"
          sx={{ 
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            fontWeight: 'bold'
          }}
        />
      );
    }
    return (
      <Chip 
        icon={<DescriptionIcon />}
        label="Template"
        size="small"
        sx={{ 
          backgroundColor: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[600]
        }}
      />
    );
  };

  const renderHeader = () => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Meus Documentos
        </Typography>
        
        <Button
          variant="contained"
          size="large"
          onClick={() => setCreateDocumentDialog(true)}
          startIcon={<AddIcon />}
          sx={{ 
            borderRadius: 3,
            px: 5,
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 700,
            letterSpacing: 0.3,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
            boxShadow: '0 8px 32px rgba(15, 23, 42, 0.4)',
            textTransform: 'none',
            color: '#ffffff',
            border: '2px solid rgba(148, 163, 184, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.3), transparent)',
              transition: 'left 0.6s ease-out',
            },
            '&:hover': {
              background: 'linear-gradient(135deg, #0c1420 0%, #1a202c 50%, #2d3748 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(15, 23, 42, 0.6)',
              borderColor: 'rgba(148, 163, 184, 0.4)',
              '&:before': {
                left: '100%',
              }
            },
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          Novo Documento
        </Button>
      </Box>

      {/* Breadcrumb Navigation */}
      {(currentFolderId || getBreadcrumbPath().length > 0) && (
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs>
            <Link
              component="button"
              variant="body2"
              onClick={() => setCurrentFolderId(null)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Início
            </Link>
            {getBreadcrumbPath().map((folder) => (
              <Link
                key={folder.id}
                component="button"
                variant="body2"
                onClick={() => setCurrentFolderId(folder.id)}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <FolderIcon sx={{ mr: 0.5, fontSize: 16 }} />
                {folder.name}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>
      )}
      
      {/* Área de drop para voltar ao nível pai (visível apenas durante drag) */}
      {draggedItem && currentFolderId && (
        <Box
          onDragOver={(e) => {
            const currentFolder = folders.find(f => f.id === currentFolderId);
            handleDragOver(e, currentFolder?.parent_id || null);
          }}
          onDragLeave={handleDragLeave}
          onDrop={(e) => {
            const currentFolder = folders.find(f => f.id === currentFolderId);
            handleDrop(e, currentFolder?.parent_id || null);
          }}
          sx={{
            mb: 2,
            p: 2,
            border: `2px dashed ${dropTarget === (folders.find(f => f.id === currentFolderId)?.parent_id || null) ? theme.palette.primary.main : theme.palette.grey[300]}`,
            borderRadius: 1,
            backgroundColor: dropTarget === (folders.find(f => f.id === currentFolderId)?.parent_id || null) ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.grey[100], 0.5),
            textAlign: 'center',
            transition: 'all 0.2s ease'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Solte aqui para mover para o nível anterior
          </Typography>
        </Box>
      )}

      {/* Controles de busca, pasta e visualização */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Buscar documentos e pastas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            minWidth: 300,
            maxWidth: 500,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.grey[100], 0.5)
            }
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={sortBy}
            label="Ordenar por"
            onChange={(e) => setSortBy(e.target.value)}
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Filtrar tipo</InputLabel>
          <Select
            value={filterType}
            label="Filtrar tipo"
            onChange={(e) => setFilterType(e.target.value)}
          >
            {typeFilterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Itens por página</InputLabel>
          <Select
            value={itemsPerPage}
            label="Itens por página"
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <MenuItem value={12}>12</MenuItem>
            <MenuItem value={18}>18</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={42}>42</MenuItem>
            <MenuItem value={60}>60</MenuItem>
          </Select>
        </FormControl>
        
        <Button
          variant="outlined"
          startIcon={<CreateNewFolderIcon />}
          onClick={() => setCreateFolderDialogOpen(true)}
          sx={{ 
            borderRadius: 2,
            px: 3,
            whiteSpace: 'nowrap'
          }}
        >
          Nova Pasta
        </Button>
        
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="grid">
            <Tooltip title="Visualização em Grade">
              <ViewModuleIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list">
            <Tooltip title="Visualização em Lista">
              <ViewListIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Informações de busca e resultados */}
      {searchTerm && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {paginatedData.totalItems > 0 ? (
              <>
                Encontrados {paginatedData.totalItems} resultado{paginatedData.totalItems !== 1 ? 's' : ''} para "{searchTerm}"
                {paginatedData.totalPages > 1 && (
                  <> • Página {currentPage} de {paginatedData.totalPages}</>
                )}
              </>
            ) : (
              <>Nenhum resultado encontrado para "{searchTerm}"</>
            )}
          </Typography>
        </Box>
      )}

      {/* Informações da pasta atual */}
      {!searchTerm && currentFolderId && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {paginatedData.totalItems} item{paginatedData.totalItems !== 1 ? 's' : ''} nesta pasta
            {paginatedData.totalPages > 1 && (
              <> • Página {currentPage} de {paginatedData.totalPages}</>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );



  const renderCategoryTabs = () => (
    <Box sx={{ mb: 3 }}>
      <Tabs
        value={selectedCategory}
        onChange={(_, newValue) => setSelectedCategory(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTab-root': {
            textTransform: 'none',
            minWidth: 'auto',
            px: 2,
            py: 1,
            borderRadius: 2,
            mr: 1,
            mb: 1,
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 'bold'
            }
          },
          '& .MuiTabs-indicator': {
            display: 'none'
          }
        }}
      >
        {documentCategories.map((category) => (
          <Tab
            key={category.id}
            value={category.id}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {category.icon}
                <span>{category.name}</span>
                <Chip 
                  label={category.count} 
                  size="small"
                  sx={{ 
                    ml: 1,
                    minWidth: 24,
                    height: 20,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
            }
          />
        ))}
      </Tabs>
    </Box>
  );

  const renderDocumentCard = (document: LegalDocument) => (
    <Card
      key={document.id}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8]
        }
      }}
      onClick={() => handlePreview(document)}
      onContextMenu={(e) => {
        e.preventDefault();
        handleMenuOpen(e, document);
      }}
    >
      <CardContent sx={{ flexGrow: 1, position: 'relative' }}>
        {/* Indicador de origem no canto superior direito */}
        <Box sx={{ 
          position: 'absolute', 
          top: 12, 
          right: 12,
          display: 'flex',
          gap: 0.5 
        }}>
          {getSourceBadge(document.source)}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuOpen(e, document);
            }}
            sx={{ 
              padding: 0.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[500], 0.1)
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Ícone do tipo de documento */}
        <Box sx={{ mb: 2, pr: 8 }}>
          <Box sx={{ 
            display: 'inline-flex',
            p: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            mb: 2
          }}>
            {document.source === 'ai_generated' ? (
              <AutoAwesomeIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
            ) : (
              <DescriptionIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
            )}
          </Box>
        </Box>

        {/* Título do documento */}
        <Typography 
          variant="h6" 
          component="h3" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            pr: 4,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {document.title}
        </Typography>

        {/* Tipo de documento */}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {document.document_type}
        </Typography>

        {/* Data de criação e atualização */}
        <Box sx={{ mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Criado em {formatDate(document.created_at)}
          </Typography>
          {document.updated_at !== document.created_at && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', opacity: 0.8 }}>
              Atualizado em {formatDate(document.updated_at)}
            </Typography>
          )}
        </Box>
      </CardContent>

      {/* Ação rápida apenas para visualizar */}
      <CardActions 
        sx={{ 
          pt: 0, 
          pb: 2, 
          px: 2,
          justifyContent: 'center'
        }}
      >
        <Button
          size="small"
          startIcon={<PreviewIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handlePreview(document);
          }}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            minWidth: 'auto',
            px: 2
          }}
        >
          Abrir Documento
        </Button>
      </CardActions>
    </Card>
  );

  const renderEmptyState = () => (
    <Box sx={{ 
      textAlign: 'center', 
      py: 8,
      px: 4,
      backgroundColor: alpha(theme.palette.grey[50], 0.5),
      borderRadius: 3,
      border: `2px dashed ${alpha(theme.palette.grey[400], 0.5)}`
    }}>
      <DescriptionIcon sx={{ 
        fontSize: 80, 
        color: alpha(theme.palette.grey[400], 0.7),
        mb: 3 
      }} />
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
        {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhum documento encontrado'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
        {searchTerm ? 
          'Tente ajustar os filtros ou termo de busca para encontrar seus documentos.' :
          'Comece criando seu primeiro documento ou use nossa IA para gerar conteúdo jurídico personalizado.'
        }
      </Typography>
      
      {!searchTerm && (
        <>
          {/* Botão promocional destacado */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/documents/flows')}
              sx={{
                borderRadius: 4,
                px: 8,
                py: 3,
                fontSize: '1.3rem',
                fontWeight: 800,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #1e40af 0%, #3730a3 50%, #581c87 100%)',
                boxShadow: '0 12px 48px rgba(30, 64, 175, 0.4)',
                position: 'relative',
                overflow: 'hidden',
                minWidth: 320,
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                  transition: 'left 0.8s ease-out'
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #4338ca 50%, #6b21a8 100%)',
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 20px 60px rgba(30, 64, 175, 0.6)',
                  '&:before': {
                    left: '100%'
                  }
                },
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Crie peças jurídicas com o poder da IA
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            ou escolha uma das opções abaixo:
          </Typography>
        </>
      )}

      <Stack direction="row" spacing={2} justifyContent="center">
        {!searchTerm && (
          <Button
            variant="outlined"
            startIcon={<CreateNewFolderIcon />}
            onClick={() => setCreateFolderDialogOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Nova Pasta
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => navigate('/documents/new')}
          sx={{ borderRadius: 2 }}
        >
          Documento em Branco
        </Button>
        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => navigate('/documents/ai-generator')}
          sx={{ borderRadius: 2 }}
        >
          Gerar com IA
        </Button>
      </Stack>
    </Box>
  );

  // ⚡ Skeleton Screen - mostra estrutura instantaneamente
  if (loading) {
    return <DocumentListSkeleton count={6} />;
  }

  return (
    <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {renderHeader()}
      {renderCategoryTabs()}

      {/* Área de drop para a raiz (quando está na raiz e arrastando) */}
      {draggedItem && !currentFolderId && (
        <Box
          onDragOver={(e) => handleDragOver(e, null)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, null)}
          sx={{
            mb: 2,
            p: 2,
            border: `2px dashed ${dropTarget === null ? theme.palette.primary.main : theme.palette.grey[300]}`,
            borderRadius: 1,
            backgroundColor: dropTarget === null ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.grey[100], 0.5),
            textAlign: 'center',
            transition: 'all 0.2s ease'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Solte aqui para mover para a raiz
          </Typography>
        </Box>
      )}

      {/* Conteúdo principal */}
      {paginatedData.totalItems === 0 ? (
        renderEmptyState()
      ) : (
        <Box>
          {viewMode === 'list' ? (
            // Visualização em Lista
            <Paper sx={{ overflow: 'hidden' }}>
              {/* Pastas */}
              {currentFolders.map((folder, index) => (
                <Box key={`folder-${folder.id}`}>
                  <Box
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'folder', folder.id)}
                  onDragOver={(e) => handleDragOver(e, folder.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, folder.id)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    cursor: 'pointer',
                    border: dropTarget === folder.id ? `2px solid ${theme.palette.primary.main}` : 'none',
                    backgroundColor: dropTarget === folder.id ? alpha(theme.palette.primary.main, 0.08) : 'inherit',
                    opacity: draggedItem?.type === 'folder' && draggedItem?.id === folder.id ? 0.5 : 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                  onClick={(e) => {
                    // Verifica se não foi clicado no botão de menu
                    if (!(e.target as Element).closest('.folder-menu-button')) {
                      setCurrentFolderId(folder.id);
                    }
                  }}
                >
                  <Box sx={{ mr: 2 }}>
                    <FolderIcon sx={{ 
                      color: theme.palette.text.secondary,
                      fontSize: 24
                    }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {folder.name}
                      {(folder.document_count ?? 0) > 0 && (
                        <Chip 
                          label={folder.document_count ?? 0} 
                          size="small" 
                          sx={{ 
                            ml: 1, 
                            height: '20px',
                            fontSize: '0.75rem',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }} 
                        />
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pasta • {new Date(folder.created_at).toLocaleDateString('pt-BR')}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    className="folder-menu-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFolderMenuOpen(e, folder);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                  {(index < currentFolders.length - 1 || filteredDocuments.length > 0) && <Divider />}
                </Box>
              ))}

              {/* Documentos */}
              {filteredDocuments.map((document, index) => (
                <Box key={`document-${document.id}`}>
                  <Box
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'document', document.id)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      cursor: 'pointer',
                      opacity: draggedItem?.type === 'document' && draggedItem?.id === document.id ? 0.5 : 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                    onClick={() => handlePreview(document)}
                  >
                    <Box sx={{ mr: 2 }}>
                      {document.source === 'ai_generated' ? (
                        <AutoAwesomeIcon sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: 24
                        }} />
                      ) : (
                        <DescriptionIcon sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: 24
                        }} />
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {document.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {document.document_type} • {formatDate(document.updated_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getSourceBadge(document.source)}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, document);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  {index < filteredDocuments.length - 1 && <Divider />}
                </Box>
              ))}
            </Paper>
          ) : (
            // Visualização em Grade
            <Grid container spacing={3}>
              {/* Pastas em grade - 5 por linha */}
              {currentFolders.map((folder) => (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={`folder-grid-${folder.id}`}>
                  <Card
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'folder', folder.id)}
                    onDragOver={(e) => handleDragOver(e, folder.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, folder.id)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      height: 180,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      border: dropTarget === folder.id ? `2px solid ${theme.palette.primary.main}` : 'none',
                      backgroundColor: dropTarget === folder.id ? alpha(theme.palette.primary.main, 0.08) : 'inherit',
                      opacity: draggedItem?.type === 'folder' && draggedItem?.id === folder.id ? 0.5 : 1,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                    onClick={(e) => {
                      // Verifica se não foi clicado no botão de menu
                      if (!(e.target as Element).closest('.folder-menu-button')) {
                        setCurrentFolderId(folder.id);
                      }
                    }}
                  >
                    <CardContent sx={{ 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      position: 'relative',
                      p: 2
                    }}>
                      {/* Menu no canto superior direito */}
                      <IconButton
                        size="small"
                        className="folder-menu-button"
                        sx={{ 
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderMenuOpen(e, folder);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>

                      {/* Ícone da pasta centralizado */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        flexGrow: 1,
                        mb: 2
                      }}>
                        <FolderIcon sx={{ 
                          fontSize: 48, 
                          color: theme.palette.text.secondary
                        }} />
                      </Box>

                      {/* Informações da pasta */}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="subtitle1" 
                          fontWeight="medium"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5
                          }}
                        >
                          {folder.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(folder.created_at).toLocaleDateString('pt-BR')}
                        </Typography>
                        {(folder.document_count ?? 0) > 0 && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              mt: 0.5,
                              color: theme.palette.primary.main,
                              fontWeight: 500
                            }}
                          >
                            {folder.document_count ?? 0} {(folder.document_count ?? 0) === 1 ? 'arquivo' : 'arquivos'}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              {/* Documentos em grade - 5 por linha */}
              {filteredDocuments.map((document) => (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={document.id}>
                  <Card
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'document', document.id)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      height: 180,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      opacity: draggedItem?.type === 'document' && draggedItem?.id === document.id ? 0.5 : 1,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                    onClick={() => handlePreview(document)}
                  >
                    <CardContent sx={{ 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      position: 'relative',
                      p: 2
                    }}>
                      {/* Badge e menu no topo */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1
                      }}>
                        {getSourceBadge(document.source)}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, document);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Ícone do documento centralizado */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        flexGrow: 1,
                        mb: 2
                      }}>
                        {document.source === 'ai_generated' ? (
                          <AutoAwesomeIcon sx={{ 
                            fontSize: 48, 
                            color: theme.palette.primary.main
                          }} />
                        ) : (
                          <DescriptionIcon sx={{ 
                            fontSize: 48, 
                            color: theme.palette.text.secondary
                          }} />
                        )}
                      </Box>

                      {/* Informações do documento */}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="subtitle1" 
                          fontWeight="medium"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5
                          }}
                        >
                          {document.title}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {document.document_type}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          
          {/* Paginação */}
          {paginatedData.totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              mt: 4,
              gap: 2
            }}>
              <Typography variant="body2" color="text.secondary">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, paginatedData.totalItems)} de {paginatedData.totalItems} itens
              </Typography>
              <Pagination
                count={paginatedData.totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                shape="rounded"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}

      {/* Menu de contexto melhorado */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 200,
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08)
              }
            }
          }
        }}
      >
        {/* Ações principais */}
        <MenuItem onClick={() => handlePreview(selectedDocument!)}>
          <PreviewIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="body2" fontWeight="medium">Visualizar</Typography>
            <Typography variant="caption" color="text.secondary">Abrir documento completo</Typography>
          </Box>
        </MenuItem>
        
        {selectedDocument?.source === 'ai_generated' && (
          <>
            <MenuItem onClick={() => handleEditDocument(selectedDocument)}>
              <EditIcon sx={{ mr: 2, color: theme.palette.success.main }} />
              <Box>
                <Typography variant="body2" fontWeight="medium">Editar</Typography>
                <Typography variant="caption" color="text.secondary">Editor de texto avançado</Typography>
              </Box>
            </MenuItem>
            
            <MenuItem onClick={() => handleDuplicateDocument(selectedDocument)}>
              <ContentCopyIcon sx={{ mr: 2, color: theme.palette.warning.main }} />
              <Box>
                <Typography variant="body2" fontWeight="medium">Duplicar</Typography>
                <Typography variant="caption" color="text.secondary">Criar cópia do documento</Typography>
              </Box>
            </MenuItem>
            
            <MenuItem onClick={() => handleFillFields(selectedDocument)}>
              <MenuBookIcon sx={{ mr: 2, color: theme.palette.info.main }} />
              <Box>
                <Typography variant="body2" fontWeight="medium">Preencher Campos</Typography>
                <Typography variant="caption" color="text.secondary">Completar informações específicas</Typography>
              </Box>
            </MenuItem>
            
            <Divider sx={{ my: 1 }} />
            
            <MenuItem onClick={() => handleDownload(selectedDocument)}>
              <DownloadIcon sx={{ mr: 2, color: theme.palette.secondary.main }} />
              <Box>
                <Typography variant="body2" fontWeight="medium">Baixar DOCX</Typography>
                <Typography variant="caption" color="text.secondary">Download em formato Word</Typography>
              </Box>
            </MenuItem>
          </>
        )}
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem 
          onClick={() => setDeleteDialogOpen(true)}
          sx={{ 
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.08)
            }
          }}
        >
          <DeleteIcon sx={{ mr: 2, color: theme.palette.error.main }} />
          <Box>
            <Typography variant="body2" fontWeight="medium" color="error.main">Excluir</Typography>
            <Typography variant="caption" color="text.secondary">Remover documento permanentemente</Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Menu de contexto das pastas */}
      <Menu
        anchorEl={folderMenuAnchor}
        open={Boolean(folderMenuAnchor)}
        onClose={handleFolderMenuClose}
      >
        <MenuItem onClick={() => setCurrentFolderId(selectedFolder?.id || null)}>
          <FolderOpenIcon sx={{ mr: 2 }} />
          Abrir Pasta
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedFolder) {
              handleDeleteFolderClick(selectedFolder);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 2 }} />
          Excluir Pasta
        </MenuItem>
      </Menu>

      {/* Dialog de confirmação para excluir pasta */}
      <Dialog
        open={deleteFolderDialogOpen}
        onClose={() => setDeleteFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Excluir Pasta "{folderToDelete?.name}"?
        </DialogTitle>
        <DialogContent>
          {folderDeleteInfo && (
            <>
              {folderDeleteInfo.document_count > 0 || folderDeleteInfo.subfolder_count > 0 ? (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>Atenção!</strong> Esta pasta contém:
                    {folderDeleteInfo.document_count > 0 && (
                      <Typography>• {folderDeleteInfo.document_count} documento(s)</Typography>
                    )}
                    {folderDeleteInfo.subfolder_count > 0 && (
                      <Typography>• {folderDeleteInfo.subfolder_count} subpasta(s)</Typography>
                    )}
                  </Alert>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Escolha uma ação:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Mover para o nível pai:</strong> Os documentos e subpastas serão movidos para o nível anterior.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <strong>Excluir tudo:</strong> A pasta e todo o seu conteúdo serão excluídos permanentemente.
                  </Typography>
                </Box>
              ) : (
                <Typography>
                  Esta pasta está vazia e será excluída.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderDialogOpen(false)}>
            Cancelar
          </Button>
          {folderDeleteInfo && (folderDeleteInfo.document_count > 0 || folderDeleteInfo.subfolder_count > 0) ? (
            <>
              <Button 
                onClick={() => confirmDeleteFolder(false)}
                color="primary"
                variant="outlined"
              >
                Mover para Nível Pai
              </Button>
              <Button 
                onClick={() => confirmDeleteFolder(true)}
                color="error"
                variant="contained"
              >
                Excluir Tudo
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => confirmDeleteFolder(false)}
              color="error"
              variant="contained"
            >
              Excluir Pasta
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog para criar pasta */}
      <Dialog 
        open={createFolderDialogOpen} 
        onClose={() => setCreateFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nova Pasta</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da pasta"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                createFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={createFolder} 
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o documento "{selectedDocument?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de escolha - Criar ou Importar */}
      <Dialog 
        open={createDocumentDialog} 
        onClose={() => setCreateDocumentDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle 
          component="div"
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1
          }}
        >
          <Typography variant="h6" component="span" fontWeight="bold">
            Escolha como deseja criar sua peça
          </Typography>
          <IconButton onClick={() => setCreateDocumentDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 4, overflow: 'visible' }}>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            {/* Opção 1: Novo Fluxo IA */}
            <Grid item xs={12} sm={6}>
              <Card 
                variant="outlined"
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
                onClick={() => {
                  setCreateDocumentDialog(false);
                  navigate('/documents/flows');
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha('#9c27b0', 0.08)} 100%)`,
                    border: '2px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.2)
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Criar com IA
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fluxos inteligentes com campos personalizados e geração avançada por IA.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Opção 2: Importar e Editar */}
            <Grid item xs={12} sm={6}>
              <Card 
                variant="outlined"
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
                onClick={() => {
                  setCreateDocumentDialog(false);
                  navigate('/documents/improve');
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'divider'
                  }}>
                    <DescriptionIcon sx={{ fontSize: 40, color: theme.palette.text.secondary }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Importar documento
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Envie um documento existente e melhore com a ajuda da Advogada Parceira.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

        </DialogContent>
      </Dialog>
    </Container>
    );
};

export default DocumentsPage; 